// チャットパネル（サイドバー上部 / UI.md §3.2）。
//
// 役割: ユーザー入力と、エージェントのメッセージ（途中経過 / 最終回答 / お知らせ）を表示する。
//       kind により表示を出し分ける: progress=控えめ、notice=注意色、最終回答=Markdown。
// 関係: messages/isRunning は App が保持。onSubmit でプロンプト送信、onAbort で中断、onReset で会話初期化。
//       API キー未設定や DuckDB 未起動のときは disabled にして理由を示す。
//
// 流用元: reference/e-Stat-Web-AI-Agent/src/components/ChatPanel.jsx（地理空間向けに簡素化）
import { useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import AgentHelpModal from './AgentHelpModal.jsx'

// input/onInputChange は App が保持（レイヤー行の「@」からも差し込めるよう持ち上げている）。
// inputRef は @ 挿入時のフォーカス用に App から渡る textarea 参照。
// voiceSlot は音声（Gemini Live）の操作 UI。Header の settingsSlot と同じスロット方式で、
// ChatPanel 側にロジックを持ち込まずに差し込む。置き場所は送信ボタンの隣（プロンプト操作列）。
function ChatPanel({
  messages,
  isRunning,
  disabled,
  disabledReason,
  input,
  onInputChange,
  inputRef,
  onSubmit,
  onAbort,
  onReset,
  voiceSlot,
}) {
  // 直前に送信した文字列。連続して同じ内容を送るのを防ぐ（誤操作による重複処理の抑止）。
  const lastSubmittedRef = useRef('')
  // 使い方（サンプルプロンプト）ダイアログの開閉。開閉状態は ChatPanel 内に閉じ込める。
  const [helpOpen, setHelpOpen] = useState(false)

  const handleSubmit = (event) => {
    event.preventDefault()
    const content = input.trim()
    if (!content || isRunning || disabled) return
    // 直前と同じ内容の連続送信はアラートで止める（入力は消さない）。
    if (content === lastSubmittedRef.current) {
      window.alert('同じ内容が連続で送信されました。内容を変えてください。')
      return
    }
    lastSubmittedRef.current = content
    onInputChange('')
    onSubmit(content)
  }

  // 新しい会話を始めるときは重複ガードをリセットする（同じ文言でも送れるように）。
  const handleReset = () => {
    lastSubmittedRef.current = ''
    onReset()
  }

  return (
    <section className="chat-panel">
      <div className="chat-header">
        <div className="chat-title">
          <h3>エージェント</h3>
          <button
            type="button"
            className="icon-btn"
            title="使い方・サンプルプロンプト"
            aria-label="使い方・サンプルプロンプト"
            onClick={() => setHelpOpen(true)}
          >
            ⓘ
          </button>
        </div>
        <div className="chat-header-actions">
          <button type="button" className="link-btn" onClick={handleReset} disabled={isRunning}>
            新しい会話
          </button>
          <span className={`chat-status${isRunning ? ' is-running' : ''}`}>
            {isRunning ? '実行中' : '待機中'}
          </span>
        </div>
      </div>

      <div className="message-list" aria-live="polite">
        {messages.length === 0 && !disabled && (
          <p className="empty-state">
            例: 「○○レイヤーから 5000m バッファを作って」「△△を 100m で簡略化して」
          </p>
        )}
        {messages.map((message) => {
          const isProgress = message.kind === 'progress'
          const isNotice = message.kind === 'notice'
          const label =
            message.role === 'user'
              ? 'You'
              : isProgress
                ? '途中経過'
                : isNotice
                  ? 'お知らせ'
                  : 'Agent'
          return (
            <article
              className={`message message-${message.role}${isProgress ? ' message-progress' : ''}${
                isNotice ? ' message-notice' : ''
              }`}
              key={message.id}
            >
              <span className="message-label">{label}</span>
              {message.role === 'assistant' && !isProgress && !isNotice ? (
                <div className="message-markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                </div>
              ) : (
                <p>{message.content}</p>
              )}
            </article>
          )
        })}
        {isRunning && (
          <div className="message-spinner" role="status">
            <span>処理中…</span>
          </div>
        )}
      </div>

      <form className="prompt-form" onSubmit={handleSubmit}>
        {disabled ? (
          <p className="prompt-disabled">{disabledReason}</p>
        ) : (
          <>
            <textarea
              ref={inputRef}
              value={input}
              rows={3}
              placeholder="自然言語で空間処理を指示…"
              onChange={(event) => onInputChange(event.target.value)}
            />
            <div className="prompt-actions">
              {voiceSlot}
              {isRunning ? (
                <button type="button" onClick={onAbort}>
                  中断
                </button>
              ) : (
                <button type="submit" disabled={!input.trim()}>
                  送信
                </button>
              )}
            </div>
          </>
        )}
      </form>

      {helpOpen && (
        <AgentHelpModal
          onClose={() => setHelpOpen(false)}
          onPick={(text) => {
            onInputChange(text)
            setHelpOpen(false)
            inputRef?.current?.focus()
          }}
        />
      )}
    </section>
  )
}

export default ChatPanel
